import { reactive } from 'vue'
import { api } from '@/lib/http.js'
import { entityPath } from '@/lib/entities.js'

/**
 * Comments on a record's fields (D-135), loaded once per record and shared by every field of
 * its form: { [`entity:uuid`]: { list, loaded } }. Fields are named as the API names them
 * ("tax_id", "cf:tier").
 */
const cache = reactive({})

export const apiField = (name) => (name.startsWith('cf.') ? `cf:${name.slice(3)}` : name)

export function commentsOf(entity, uuid) {
  const key = `${entity}:${uuid}`
  if (!cache[key]) {
    cache[key] = { list: [], loaded: false, loading: null }
  }
  const state = cache[key]
  if (!state.loaded && !state.loading) {
    state.loading = api(`/${entityPath(entity)}/${uuid}/field-comments`)
      .then((res) => {
        state.list = res.data
        state.loaded = true
      })
      .catch(() => {
        state.loaded = true
      })
      .finally(() => {
        state.loading = null
      })
  }
  return state
}

/** Read a record's comments again: opening its card shows what others wrote meanwhile. */
export function refreshComments(entity, uuid) {
  const state = cache[`${entity}:${uuid}`]
  if (state && !state.loading) state.loaded = false
  return commentsOf(entity, uuid)
}

/** `doc` is the rich-text document of the comment (with @mentions); `label` the field's name as shown. */
export async function addComment(entity, uuid, field, doc, isPrivate, label = null) {
  const created = (await api(`/${entityPath(entity)}/${uuid}/field-comments`, { method: 'POST', body: { field, body_doc: doc, is_private: isPrivate, field_label: label } })).data
  commentsOf(entity, uuid).list.push(created)
}

export async function changeComment(entity, uuid, comment, patch) {
  const updated = (await api(`/field-comments/${comment.uuid}`, { method: 'PATCH', body: patch })).data
  const state = commentsOf(entity, uuid)
  state.list = state.list.map((c) => (c.uuid === updated.uuid ? updated : c))
}

export async function removeComment(entity, uuid, comment) {
  await api(`/field-comments/${comment.uuid}`, { method: 'DELETE' })
  const state = commentsOf(entity, uuid)
  state.list = state.list.filter((c) => c.uuid !== comment.uuid)
}
